/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2025, Zumper
 * @description ZoeHeaderModal
 */

import { ChevronLeft, MapPin, Search, ZoeSparkle } from '@zcode-ui/icons'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  ToggleGroup,
  ToggleGroupItem,
  useDisclosure,
} from '@zcode-ui/shadz-ui'
import {
  SEARCH_BAR_CLICK,
  ZOE_ENTRY_SEARCH_BAR_CLICK,
  ZOE_TOGGLE_CLICK,
} from '@zcode-ui/web-zumper-integrations'
import type { AutocompleteApiResult, AutocompleteResult } from '@zcode/bizos'
import { EVENT_NAMES, PAGE_NAMES, PROP_NAMES } from '@zcode/mixpanel'
import { type Origin, localStorageSingleton } from '@zcode/nr-utils'
import { SharedAutocomplete } from '@zcode/nr/es/shared/components/Autocomplete/SharedAutocomplete'
import type { AIAssistantFiltersResponse, Dispatch, GetState } from '@zcode/state-type'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { ZoeContentInput } from '../ZoeContentInput/ZoeContentInput'
import { trackMapListZoeEvent } from '../module/thunks/trackMapListZoeEvent'
import theme from './autocomplete.module.scss'

interface Props {
  dispatchMixpanelAutocompleteResultClick: ({
    result,
    index,
  }: {
    result: AutocompleteApiResult
    index: number
  }) => void
  getAIAssistantSearchFilters: (
    searchQuery: string
  ) => (dispatch: Dispatch, getState: GetState) => Promise<AIAssistantFiltersResponse | undefined>
  mixpanelEvent?: (
    event: (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES],
    origin: Origin | undefined,
    extraPayload?: object
  ) => (dispatch: Dispatch, getState: GetState) => void
  onCloseClick?: () => void
  onSelectAutocompleteResult: (result: AutocompleteResult) => void
  placeholder: string
  variation: string | undefined | null
}

export const ZoeHeaderModal = ({
  dispatchMixpanelAutocompleteResultClick,
  getAIAssistantSearchFilters,
  mixpanelEvent,
  onCloseClick,
  onSelectAutocompleteResult,
  placeholder,
  variation,
}: Props) => {
  const dispatch = useDispatch()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const prevSelectedToggle: string | undefined = localStorageSingleton.get('zoePopoverToggle')
  const defaultToggle = prevSelectedToggle
    ? prevSelectedToggle
    : variation === 'SHOW_ZOE_AUTOCOMPLETE_DEFAULT'
      ? 'zoe'
      : 'location'
  const [selectedToggle, setSelectedToggle] = useState(defaultToggle)

  const handleClose = () => {
    onCloseClick?.()
    onClose()
    setTimeout(() => {
      setSelectedToggle(defaultToggle)
    }, 500)
  }
  const handleSelectAutocompleteResult = (result: AutocompleteResult) => {
    onSelectAutocompleteResult(result)
    handleClose()
  }

  const handleFocus = () => {
    dispatch(
      trackMapListZoeEvent({
        eventName: ZOE_ENTRY_SEARCH_BAR_CLICK,
        additionalProps: {
          listName: 'mapList',
        },
      })
    )
    mixpanelEvent &&
      dispatch(
        mixpanelEvent(EVENT_NAMES.CLICK, undefined, {
          [PROP_NAMES.BUTTON_CLICKED]: 'Zoe Entry Search Bar',
        })
      )
    onOpen()
  }

  const trackToggleClick = (value: 'zoe' | 'location') => {
    if (value === 'location') {
      dispatch(
        trackMapListZoeEvent({
          eventName: SEARCH_BAR_CLICK,
          additionalProps: {
            pageType: PAGE_NAMES.SEARCH,
          },
        })
      )
    }

    dispatch(
      trackMapListZoeEvent({
        eventName: ZOE_TOGGLE_CLICK,
        additionalProps: {
          property: value === 'zoe' ? 'Ask Zoe on Maplist' : 'Location on Maplist',
        },
      })
    )
    mixpanelEvent &&
      dispatch(
        mixpanelEvent(EVENT_NAMES.ZOE_TOGGLE_CLICK, undefined, {
          [PROP_NAMES.BUTTON_CLICKED]:
            value === 'zoe' ? 'Ask Zoe on Maplist' : 'Location on Maplist',
        })
      )

    setSelectedToggle(value)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose()
        } else {
          const div = document.getElementById('zoe-content-input')
          if (div) {
            div.setAttribute('tabindex', '-1')
            div.focus()
          }
        }
      }}
    >
      <DialogTrigger
        className="absolute bottom-0 translate-y-1/2"
        style={{
          width: 'calc(100% - 48px)',
        }}
      >
        <div className="relative">
          <Input
            className="cursor-text [&::placeholder]:text-gray-600 text-base leading-6 h-12 border-gray-150 py-3 pr-[18px] pl-12"
            onFocus={handleFocus}
            placeholder="What are you looking for?"
          />
          <span className="absolute top-[15px] left-6">
            <Search size={14} fill="#726F6C" />
          </span>
        </div>
      </DialogTrigger>
      <DialogContent
        className="z-popover h-full p-0 [&>button]:hidden bg-gray-50"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="p-0 absolute h-[68px] left-0 top-0 right-0 justify-center">
          <DialogClose data-testid="zoe-header-modal-close" asChild>
            <button className="absolute left-4 w-10 h-10" onClick={handleClose} type="button">
              <ChevronLeft fill="#726F6C" />
            </button>
          </DialogClose>
          <DialogTitle asChild>
            <p className="text-gray-950 text-sm font-extrabold !tracking-[1.96px] leading-5 uppercase">
              Search
            </p>
          </DialogTitle>
        </DialogHeader>
        <div className="pt-[68px]">
          <ToggleGroup
            className="gap-0 px-6 w-full"
            onValueChange={trackToggleClick}
            type="single"
            value={selectedToggle}
          >
            {variation === 'SHOW_ZOE_AUTOCOMPLETE_DEFAULT' && (
              <>
                <ToggleGroupItem
                  className="gap-1 w-1/2 rounded-l-lg rounded-r-none hover:bg-gray-50 hover:text-gray-80"
                  disabled={selectedToggle === 'zoe'}
                  size="sm"
                  value="zoe"
                  variant="zumper"
                >
                  <ZoeSparkle size={20} />
                  <span className="font-extrabold">Ask Zoe</span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  className="gap-[2px] w-1/2 border-l-0 rounded-r-lg rounded-l-none hover:bg-gray-50 hover:text-gray-80 [&>svg]:!h-[14px]"
                  disabled={selectedToggle === 'location'}
                  size="sm"
                  value="location"
                  variant="zumper"
                >
                  <MapPin size={12} fill={selectedToggle === 'location' ? '#fff' : '#454442'} />
                  <span className="font-extrabold">Location</span>
                </ToggleGroupItem>
              </>
            )}
            {variation === 'SHOW_ZOE' && (
              <>
                <ToggleGroupItem
                  className="gap-1 w-1/2 rounded-l-lg rounded-r-none hover:bg-gray-50 hover:text-gray-80"
                  disabled={selectedToggle === 'location'}
                  size="sm"
                  value="location"
                  variant="zumper"
                >
                  {' '}
                  <MapPin size={12} fill={selectedToggle === 'location' ? '#fff' : '#454442'} />
                  <span className="font-extrabold">Location</span>
                </ToggleGroupItem>
                <ToggleGroupItem
                  className="gap-[2px] w-1/2 border-l-0 rounded-r-lg rounded-l-none hover:bg-gray-50 hover:text-gray-80 [&>svg]:!h-[14px]"
                  disabled={selectedToggle === 'zoe'}
                  size="sm"
                  value="zoe"
                  variant="zumper"
                >
                  <ZoeSparkle size={20} />
                  <span className="font-extrabold">Ask Zoe</span>
                </ToggleGroupItem>
              </>
            )}
          </ToggleGroup>
          {selectedToggle === 'zoe' && (
            <ZoeContentInput
              getAIAssistantSearchFilters={getAIAssistantSearchFilters}
              handleClose={onClose}
              isDrawer
              isFromSearchBar
              mixpanelEvent={mixpanelEvent}
            />
          )}
          {selectedToggle === 'location' && (
            <div className="relative pb-4 pt-3 [&>div>form]:px-4 [&>div>form>button]:right-4 [&>div>form>button]:h-14 [&>div>form>button]:w-14 [&_form_input[type=search]]:py-4 [&_form]:relative">
              <div className="absolute pt-1 left-10 top-[32.5px] z-100">
                <Search fill="#726F6C" />
              </div>
              <SharedAutocomplete
                dispatchMixpanelAutocompleteResultClick={dispatchMixpanelAutocompleteResultClick}
                isMapListNav
                isMobile
                onSelectAutocompleteResult={handleSelectAutocompleteResult}
                origin={'origin-fixed'}
                pageName={PAGE_NAMES.SEARCH_MAP}
                placeholder={placeholder}
                showMobileAutocomplete
                theme={theme}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
